/**
 * useFollowups — TanStack Query wrappers for the followups admin UI
 * (Story 7.14b). Wraps the typed `followupsApi` client so the page/detail
 * components stay focused on rendering.
 *
 * Query keys
 *   - ['followups', 'list', qs]     → list query, scoped by querystring
 *   - ['followups', 'aggregate', '24h'] → 5 KPI cards (auto-refetch 30s)
 *   - ['followup',  id]             → detail query, scoped by id
 *
 * Mutations: retry lives in `RetryFollowupModal` — it's invoked from
 * ConversationDetail (Story 7.10b) so we expose `useRetryFollowup` here as
 * a thin wrapper that the modal can call. Invalidates both
 * `['followups', 'list']` (list view) and the conversation-scoped key.
 */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  followupsApi,
  type FollowupAggregate24h,
  type FollowupDetail,
  type FollowupListResponse,
} from '@/lib/followups';

export function useFollowupsList(qs: string) {
  return useQuery<FollowupListResponse>({
    queryKey: ['followups', 'list', qs],
    queryFn: () => followupsApi.list(qs),
    placeholderData: keepPreviousData,
  });
}

export function useFollowupDetail(id: string | undefined) {
  return useQuery<FollowupDetail>({
    queryKey: ['followup', id],
    queryFn: () => followupsApi.get(id as string),
    enabled: Boolean(id),
  });
}

export function useFollowupAggregate24h() {
  return useQuery<FollowupAggregate24h>({
    queryKey: ['followups', 'aggregate', '24h'],
    queryFn: () => followupsApi.aggregate24h(),
    refetchInterval: 30_000,
  });
}

export interface UseRetryFollowupOpts {
  onSuccess?: () => void;
  onError?: (err: unknown) => void;
}

export function useRetryFollowup(
  conversationId: string | undefined,
  opts: UseRetryFollowupOpts = {},
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => followupsApi.retry(conversationId as string),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['followups', 'list'] });
      qc.invalidateQueries({ queryKey: ['followups', 'aggregate'] });
      qc.invalidateQueries({ queryKey: ['conversation', conversationId] });
      opts.onSuccess?.();
    },
    onError: (err) => opts.onError?.(err),
  });
}
